// src/components/property/ImageProcessingControls.tsx
/**
 * ImageProcessingControls Component
 *
 * Controls for aspect ratio selection, process button, and show original images button.
 */

import { Loader2, Images } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import type { IImageProcessingControlsProps, IImageAspectRatio } from '@/@types/property';

function ImageProcessingControls({
  selectedAspectRatio,
  isProcessing,
  progress,
  onAspectRatioChange,
  onProcess,
  onShowOriginalImages,
}: IImageProcessingControlsProps) {
  return (
    <div className="flex flex-wrap items-center gap-4 p-4 bg-muted/50 rounded-lg">
      {/* Show Original Images Button */}
      <Button
        variant="outline"
        onClick={onShowOriginalImages}
        disabled={isProcessing}
        className="gap-2"
      >
        <Images className="h-4 w-4" />
        Original Images
      </Button>

      {/* Aspect Ratio Selector */}
      <div className="flex items-center gap-2">
        <span className="text-sm font-medium">Aspect Ratio:</span>
        <Select
          value={selectedAspectRatio}
          onValueChange={value => onAspectRatioChange(value as IImageAspectRatio)}
          disabled={isProcessing}
        >
          <SelectTrigger className="w-[100px]">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="9:16">9:16</SelectItem>
            <SelectItem value="16:9">16:9</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Process Button */}
      <Button onClick={onProcess} disabled={isProcessing} className="ml-auto">
        {isProcessing ? (
          <>
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            {progress && progress.total > 0 ? (
              <span>
                Processing {progress.completed}/{progress.total}...
              </span>
            ) : (
              <span>Processing...</span>
            )}
          </>
        ) : (
          'Process Images'
        )}
      </Button>
    </div>
  );
}

export { ImageProcessingControls };
